/**
 * video-editor/draft.js — смонтированная лента переживает F5, падение вкладки и
 * закрытый браузер.
 *
 * Лента живёт в памяти вкладки: случайная перезагрузка стирала весь монтаж —
 * порядок клипов, обрезки, переходы, титры, музыку. Хранит её общий примитив
 * `__toolDraft` (public/js/tool-draft.js поверх work-storage.js, bucket `work`):
 * состояние проекта и БАЙТЫ файлов одной транзакцией, через полсекунды после
 * правки и сразу при уходе со страницы. Разбор примитива —
 * docs/why/primitives/tool-draft.md; образец раскладки — /thumbnail-maker.
 *
 * ⚠️ Видео бывают по гигабайту. Chromium хранит КОПИЮ байтов файла, поэтому
 * черновик берёт файл, только если он не больше FILE_CAP и вместе с остальными
 * не больше DRAFT_CAP. Остальные файлы черновик помнит по имени и размеру: лента
 * возвращается целиком, а такие клипы просят добавить файл снова — и встают на
 * свои места, когда человек его выберет.
 *
 * window.VideoEditorDraft.create({
 *   slug, t(key, fallback), track(f, data), toast(msg, type),
 *   payload()  → {state, files, label, preview, meta} | null,
 *   apply(d)   → Promise   — положить сохранённый проект на ленту,
 *   busy()     → bool       — человек уже начал своё (не открывать чужое поверх),
 *   notice(text, kind)      — строка над лентой (kind: 'restored'|'info'|'lost'|null)
 * }) → { touch(counted), exported(), boot(), fresh(), open(id), remove(id), list(), rows(), current() }
 */
(function () {
'use strict';
if (window.VideoEditorDraft) return;

var FILE_CAP = 512 * 1048576;          // байт на один файл в черновике
var DRAFT_CAP = 1536 * 1048576;        // байт на весь черновик
/* No count cap and no deletion by age (they were 6 and 30 days until 18.10.2026; three
   people had filled the six): past PROJECTS_BUDGET (each shared file once) the oldest
   projects stop keeping their files and keep everything else — the timeline comes back
   and asks for the clips, the same way as for a file over FILE_CAP. */
var PROJECTS_BUDGET = 4 * 1024 * 1048576;
var REOPEN_MS = 7 * 864e5;             // незаконченный проект старше — ждёт в списке
var OPEN_KEY = 've-draft-open';        // sessionStorage: какой проект открыт в ЭТОЙ вкладке

function newId() { return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
function rememberOpen(id) { try { if (id) sessionStorage.setItem(OPEN_KEY, id); else sessionStorage.removeItem(OPEN_KEY); } catch (_) {} }
function lastOpen() { try { return sessionStorage.getItem(OPEN_KEY) || ''; } catch (_) { return ''; } }

/** Какие файлы черновик может унести байтами: по порядку, пока влезают. */
function pickFiles(list) {
    var total = 0, out = [];
    list.forEach(function (it) {
        var sz = (it.file && it.file.size) || 0;
        var ok = !!it.file && sz <= FILE_CAP && total + sz <= DRAFT_CAP;
        if (ok) total += sz;
        out.push(ok);
    });
    return out;
}

function create(o) {
    var S = null, tried = false;
    var D = { id: null, edits: 0, editedAt: 0, exportedAt: 0, auto: false, editsAtOpen: 0, rows: [], quiet: 0, live: false };

    function store() {
        if (tried) return S;
        tried = true;
        try {
            if (window.__toolDraft) S = window.__toolDraft.store(o.slug, {
                bytesBudget: PROJECTS_BUDGET, maxAgeMs: Infinity, debounceMs: 600, onConflict: forked,
            });
        } catch (e) { S = null; }
        return S;
    }

    function saved(r) {
        if (!r) return;
        if (r.ok) {
            if (r.degraded) o.track('draft_degraded', { why: String(r.degraded).slice(0, 30) });
            return;
        }
        if (r.why === 'foreign') return;
        o.track('draft_save_failed', { why: String(r.why || '').slice(0, 30) });
    }

    function payload() {
        var p = o.payload();
        if (!p) return null;
        p.meta = Object.assign({}, p.meta || {}, { edits: D.edits, editedAt: D.editedAt, exportedAt: D.exportedAt });
        return p;
    }

    /** Правка ленты. counted — шаг истории (а не ползунок на ходу). */
    function touch(counted) {
        if (!D.live || D.quiet) return;
        var s = store(); if (!s) return;
        if (!D.id) {
            if (!counted) return;
            D.id = newId(); D.auto = false;
            s.claim(D.id);
            rememberOpen(D.id);
        }
        if (counted) { D.edits++; D.editedAt = Date.now(); }
        s.schedule(D.id, payload, saved);
    }

    /* Тот же проект открыт в другой вкладке или записан после нас: чужое не
       затираем. Если эта вкладка лишь открыла его при входе и ничего в него не
       внесла — отдаём его той вкладке; иначе продолжаем копией. */
    function forked(id, how) {
        if (id !== D.id) return;
        var s = store(); if (!s) return;
        if (D.auto && D.edits === D.editsAtOpen) {
            s.cancelScheduled(); s.release(id);
            D.id = null; D.auto = false;
            rememberOpen(null);
            o.notice(o.t('draft_open_elsewhere', 'This project is already open in another tab.'), 'info');
            o.track('draft_yielded', { how: how });
            if (o.reset) { D.quiet++; try { o.reset(); } finally { D.quiet--; } }
            return;
        }
        D.id = newId(); D.auto = false;
        s.claim(D.id);
        rememberOpen(D.id);
        D.editedAt = Date.now();
        s.schedule(D.id, payload, saved);
        o.toast(o.t('draft_forked', 'This project is also open in another tab — your changes here are saved as a copy.'), 'info');
        o.track('draft_forked', { how: how });
    }

    function exported() {
        var s = store(); if (!s || !D.live) return;
        if (!D.id) { D.id = newId(); s.claim(D.id); rememberOpen(D.id); }
        D.exportedAt = Date.now();
        s.schedule(D.id, payload, saved);
    }

    function unfinished(row) {
        var m = row.meta || {};
        return (m.edits || 0) >= 2 && (m.editedAt || 0) > (m.exportedAt || 0) && Date.now() - (row.savedAt || 0) < REOPEN_MS;
    }

    function open(id, opts) {
        opts = opts || {};
        var s = store(); if (!s) return Promise.resolve(false);
        // partial: a file that could not be kept (unreadable when it was saved, or released by
        // the byte budget) comes back as missing in its place — the rest of the project stays.
        return Promise.all([s.load(id, { partial: true }), opts.busy ? Promise.resolve(opts.busy) : s.claimedElsewhere()]).then(function (r) {
            var d = r[0], busy = r[1];
            if (!d) { if (!opts.auto) o.toast(o.t('draft_open_failed', 'Could not open this project.'), 'error'); return false; }
            if (opts.auto && (D.id || o.busy())) return false;
            return s.flush().then(function () {
                if (D.id) s.release(D.id);
                D.quiet++;
                return Promise.resolve(o.apply(d)).then(function (res) { D.quiet--; return res; }, function (e) { D.quiet--; throw e; });
            }).then(function (res) {
                var m = d.meta || {}, fork = !!busy[id];
                D.id = fork ? newId() : id;
                D.auto = !!opts.auto && !fork;
                D.edits = m.edits || 0; D.editsAtOpen = D.edits;
                D.editedAt = m.editedAt || d.savedAt || 0;
                D.exportedAt = m.exportedAt || 0;
                rememberOpen(D.id);
                s.claim(D.id).then(function (ok) { if (!ok) forked(D.id, 'claim'); });
                if (fork) {
                    s.schedule(D.id, payload, saved);
                    o.toast(o.t('draft_forked', 'This project is also open in another tab — your changes here are saved as a copy.'), 'info');
                }
                var missing = (res && res.missing) || [];
                if (opts.auto) o.notice(o.t('draft_restored', 'Your project from :time is back — continue where you left off.').replace(':time', when(m.editedAt || d.savedAt)), 'restored');
                o.track(opts.auto ? 'draft_reopened' : 'draft_open', { age_min: Math.round((d.ageMs || 0) / 60000), missing: missing.length, fork: fork ? 1 : 0, bytes: d.withFiles ? 1 : 0 });
                return true;
            });
        }).catch(function (e) {
            o.track('draft_open_error', { err: String(e && e.message || e).slice(0, 80) });
            if (!opts.auto) o.toast(o.t('draft_open_failed', 'Could not open this project.'), 'error');
            return false;
        });
    }

    /** «Новый монтаж»: текущий проект остаётся в списке, лента чистая. */
    function fresh() {
        var s = store();
        if (s && D.id) { s.flush(); s.release(D.id); }
        D.id = null; D.edits = 0; D.editedAt = 0; D.exportedAt = 0; D.auto = false;
        rememberOpen(null);
    }

    function list() {
        var s = store(); if (!s) return Promise.resolve([]);
        return s.flush().then(function () { return Promise.all([s.list(), s.claimedElsewhere()]); }).then(function (r) {
            D.rows = r[0];
            return { rows: r[0], busy: r[1] };
        });
    }
    function remove(id) {
        var s = store(); if (!s) return Promise.resolve();
        if (id === D.id) { s.cancelScheduled(); s.release(id); D.id = null; D.auto = false; rememberOpen(null); }
        return s.remove(id);
    }

    function when(ts) {
        try { return new Date(ts).toLocaleString(window.__LOCALE || undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
        catch (_) { return new Date(ts).toLocaleString(); }
    }
    function idle(ms) {
        return new Promise(function (resolve) {
            var done = false, go = function () { if (!done) { done = true; resolve(); } };
            try { if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: ms }); } catch (_) {}
            setTimeout(go, ms);
        });
    }

    /* После загрузки: сказать, что забрал браузер, и открыть проект, над
       которым работали, — если человек ещё не начал своё. */
    function boot() {
        var s = store();
        if (!s) return Promise.resolve();
        D.live = true;
        return s.takeLost().then(function (lost) {
            if (lost.length) {
                o.notice(o.t('draft_lost', 'The browser deleted saved projects to free up disk space.'), 'lost');
                o.track('draft_lost', { n: lost.length });
            }
            // «Открыт ли в другой вкладке?» — когда страница осела: фоновая
            // вкладка отвечает поздно, пока передняя грузится (одна нить).
            return idle(1500);
        }).then(function () {
            return Promise.all([s.list(), s.claimedElsewhere(600)]);
        }).then(function (r) {
            var rows = r[0], busy = r[1];
            D.rows = rows;
            if (D.id || o.busy()) return;
            var own = lastOpen(), pick = null;
            if (own) pick = rows.filter(function (x) { return x.id === own; })[0] || null;
            if (pick && busy[pick.id]) pick = null;
            if (!pick) rows.forEach(function (x) {
                if (busy[x.id] || !unfinished(x)) return;
                if (!pick || ((x.meta || {}).editedAt || 0) > ((pick.meta || {}).editedAt || 0)) pick = x;
            });
            if (pick) return open(pick.id, { auto: true, busy: busy });
        }).catch(function () {});
    }

    return {
        touch: touch, exported: exported, boot: boot, fresh: fresh, open: open, remove: remove, list: list,
        rows: function () { return D.rows; },
        current: function () { return D.id; },
        available: function () { return !!store(); },
        when: when,
    };
}

window.VideoEditorDraft = { create: create, pickFiles: pickFiles, FILE_CAP: FILE_CAP, DRAFT_CAP: DRAFT_CAP };
})();
